import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import type { MutableRefObject } from "react";
import { useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { opinionHeaderAtom } from "~/components/article/article-render/opinion-article/header/atoms";
import { checkIsOpinionArticle } from "~/components/article/article-render/opinion-article/helpers";
import { useFlag } from "~/lib/feature-flag";
import type { showTypeformArticle$key } from "~/queries/__generated__/showTypeformArticle.graphql";

type NonEmptyArray<T> = [T, ...T[]];

export const useIsShowTypeform = <T>(
  currentArticle: showTypeformArticle$key,
  itemElementReferences: MutableRefObject<(Element | null)[]>,
  items: NonEmptyArray<T>,
) => {
  const article = useFragment(
    graphql`
      fragment showTypeformArticle on Article {
        ...helpersCheckIsOpinionArticle
      }
    `,
    currentArticle,
  );

  const [isFiftyPercentageIntersect, setIsFiftyPercentageIntersect] = useState(false);
  const [isHundredPercentageIntersect, setIsHundredPercentageIntersect] = useState(false);

  const isTypeformEnabled = useFlag("enable_typeform");
  const [isShowTypeform, setIsShowTypeform] = useState(false);

  const isOpinionArticle = checkIsOpinionArticle(article);

  useIsomorphicLayoutEffect(() => {
    if (!isTypeformEnabled || !isOpinionArticle) {
      return;
    }

    const fiftyPercentageObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsFiftyPercentageIntersect(entry.isIntersecting);
        });
      },
      { rootMargin: "0px 0px 80% 0px" },
    );

    const hundredPercentageObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsFiftyPercentageIntersect(false);
          setIsHundredPercentageIntersect(entry.isIntersecting);
        });
      },
      { rootMargin: "0px 0px -50% 0px" },
    );

    itemElementReferences.current.forEach(reference => {
      if (!reference) return;

      const fiftyPercentageTarget = reference.querySelector(
        ".js-reading-50-percent-completion-tracker",
      );
      const hundredPercentageTarget = reference.querySelector(
        ".js-reading-100-percent-completion-tracker",
      );
      fiftyPercentageTarget && fiftyPercentageObserver.observe(fiftyPercentageTarget);
      hundredPercentageTarget && hundredPercentageObserver.observe(hundredPercentageTarget);
    });

    return () => {
      fiftyPercentageObserver.disconnect();
      hundredPercentageObserver.disconnect();
    };
  }, [itemElementReferences, isTypeformEnabled, isOpinionArticle, items]);

  const opinionHeader = useAtomValue(opinionHeaderAtom);
  useEffect(() => {
    if (isShowTypeform) {
      if (
        opinionHeader?.isShowingOpinionHeaderContainer ||
        (isHundredPercentageIntersect && !isFiftyPercentageIntersect)
      ) {
        setIsShowTypeform(false);
      }
    } else {
      if (isFiftyPercentageIntersect) {
        setIsShowTypeform(true);
      }
    }
  }, [opinionHeader, isShowTypeform, isFiftyPercentageIntersect, isHundredPercentageIntersect]);

  return { isShowTypeform: isOpinionArticle && isShowTypeform };
};
